/**
 * ROICalculator - Interaktywny kalkulator ROI dla landing pages
 * Wersje: hodowcy (breeder) i firmy usługowe (services)
 */
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, TrendingUp, Users, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

// Konfiguracja dla różnych typów biznesu
const CONFIG = {
  breeder: {
    title: "ApisAI pomaga hodowcom sprzedawać szczenięta i promować reproduktory",
    subtitle: "Zbierając zapytania bezpośrednio do nich",
    inputs: [
      { 
        key: 'littersPerYear', 
        label: 'Ile miotów rocznie?', 
        min: 1, 
        max: 12, 
        default: 2,
        unit: 'miotów'
      },
      { 
        key: 'puppiesPerLitter', 
        label: 'Średnio szczeniąt w miocie', 
        min: 2, 
        max: 12, 
        default: 6,
        unit: 'szczeniąt'
      },
      { 
        key: 'pricePerPuppy', 
        label: 'Średnia cena szczenięcia', 
        min: 2000, 
        max: 15000, 
        step: 500,
        default: 5000,
        unit: 'zł'
      }
    ],
    calculate: (values) => {
      const { littersPerYear, puppiesPerLitter, pricePerPuppy } = values;
      const totalPuppies = littersPerYear * puppiesPerLitter;
      const currentRevenue = totalPuppies * pricePerPuppy;
      
      // Założenia Boostera: +40% więcej zapytań, +25% szybsza sprzedaż
      const boosterLeadsIncrease = 0.40;
      const fasterSalesBonus = 0.15; // mniej czasu = mniej kosztów utrzymania
      
      const additionalLeads = Math.round(totalPuppies * boosterLeadsIncrease);
      const potentialExtraSales = Math.round(additionalLeads * 0.6); // 60% konwersja
      const extraRevenue = potentialExtraSales * pricePerPuppy;
      const timeSavings = littersPerYear * 15; // 15h oszczędności na miot
      
      return {
        currentRevenue,
        extraRevenue,
        totalWithBooster: currentRevenue + extraRevenue,
        additionalLeads: Math.round(totalPuppies * 2.4), // ~2.4 zapytań na szczenię
        timeSavings,
        roi: Math.round((extraRevenue / (199 * 12)) * 100) // ROI vs koszt roczny
      };
    },
    resultLabels: {
      extraRevenue: "Potencjalny dodatkowy przychód",
      additionalLeads: "Więcej zapytań rocznie",
      timeSavings: "Godzin zaoszczędzonych",
      roi: "Zwrot z inwestycji"
    }
  },
  services: {
    title: "Policz swój potencjalny zysk",
    subtitle: "Zobacz, ile klientów możesz pozyskać",
    inputs: [
      { 
        key: 'servicesPerMonth', 
        label: 'Usług miesięcznie', 
        min: 5, 
        max: 100, 
        default: 20,
        unit: 'usług'
      },
      { 
        key: 'avgServicePrice', 
        label: 'Średnia cena usługi', 
        min: 50, 
        max: 2000, 
        step: 50,
        default: 200,
        unit: 'zł'
      },
      { 
        key: 'clientsFromInternet', 
        label: 'Klientów z internetu (%)', 
        min: 0, 
        max: 100, 
        step: 5,
        default: 20,
        unit: '%'
      }
    ],
    calculate: (values) => {
      const { servicesPerMonth, avgServicePrice, clientsFromInternet } = values;
      const currentMonthlyRevenue = servicesPerMonth * avgServicePrice;
      const currentInternetRevenue = currentMonthlyRevenue * (clientsFromInternet / 100);
      
      // Założenia Boostera: 3x więcej klientów z internetu
      const boosterMultiplier = 3;
      const newInternetRevenue = currentInternetRevenue * boosterMultiplier;
      const extraMonthlyRevenue = newInternetRevenue - currentInternetRevenue;
      
      return {
        currentRevenue: currentMonthlyRevenue * 12,
        extraRevenue: extraMonthlyRevenue * 12,
        totalWithBooster: (currentMonthlyRevenue + extraMonthlyRevenue) * 12,
        additionalLeads: Math.round(servicesPerMonth * 0.5 * 12), // ~0.5 nowego leada na usługę
        timeSavings: 12 * 8, // 8h miesięcznie na marketing
        roi: Math.round((extraMonthlyRevenue * 12) / (199 * 12) * 100)
      };
    },
    resultLabels: {
      extraRevenue: "Dodatkowy przychód rocznie",
      additionalLeads: "Nowych zapytań rocznie",
      timeSavings: "Godzin zaoszczędzonych",
      roi: "Zwrot z inwestycji"
    }
  }
};

function SliderInput({ label, value, onChange, min, max, step = 1, unit, accentColor = 'orange' }) {
  const percentage = ((value - min) / (max - min)) * 100;
  const colorHex = accentColor === 'violet' ? '#F47A3B' : '#F47A3B';
  
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-zinc-700">{label}</label>
        <div className="text-lg font-bold text-zinc-900">
          {value.toLocaleString('pl-PL')} {unit}
        </div>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`w-full h-2 bg-zinc-200 rounded-lg appearance-none cursor-pointer ${accentColor === 'violet' ? 'slider-thumb' : 'slider-thumb'}`}
          style={{
            background: `linear-gradient(to right, ${colorHex} ${percentage}%, #e5e7eb ${percentage}%)`
          }}
        />
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, suffix = '', color = 'orange', delay = 0 }) {
  const colorClasses = {
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    violet: 'bg-orange-50 text-orange-600 border-orange-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`p-4 rounded-xl border ${colorClasses[color]}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          color === 'orange' ? 'bg-orange-100' :
          color === 'green' ? 'bg-emerald-100' :
          color === 'violet' ? 'bg-orange-100' : 'bg-blue-100'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">
            {typeof value === 'number' ? value.toLocaleString('pl-PL') : value}{suffix}
          </div>
          <div className="text-sm opacity-80">{label}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function ROICalculator({ type = 'breeder', accentColor = 'orange' }) {
  const config = CONFIG[type];
  
  // Initialize state with default values
  const [values, setValues] = useState(() => {
    const initial = {};
    config.inputs.forEach(input => {
      initial[input.key] = input.default;
    });
    return initial;
  });
  
  // Always show results - they update automatically
  const [showResults, setShowResults] = useState(true);
  
  const results = useMemo(() => config.calculate(values), [values, config]);
  
  const handleInputChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };
  
  const accentClasses = {
    orange: {
      bg: 'bg-gradient-to-br from-orange-50 to-amber-50',
      border: 'border-orange-200',
      button: 'bg-[#F7931E] hover:bg-[#E8820D]',
      text: 'text-[#F7931E]'
    },
    violet: {
      bg: 'bg-gradient-to-br from-orange-50 to-purple-50',
      border: 'border-orange-200',
      button: 'bg-orange-600 hover:bg-orange-700',
      text: 'text-orange-600'
    }
  };
  
  const accent = accentClasses[accentColor];

  return (
    <div className={`rounded-3xl ${accent.bg} ${accent.border} border-2 p-6 sm:p-8`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
          accentColor === 'orange' ? 'bg-orange-100 text-orange-700' : 'bg-orange-100 text-orange-700'
        } text-sm font-medium mb-4`}>
          <Calculator className="w-4 h-4" />
          Kalkulator ROI
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">
          {config.title}
        </h3>
        <p className="text-zinc-600">{config.subtitle}</p>
      </div>
      
      {/* Inputs */}
      <div className="space-y-6 mb-8">
        {config.inputs.map((input) => (
          <SliderInput
            key={input.key}
            label={input.label}
            value={values[input.key]}
            onChange={(val) => handleInputChange(input.key, val)}
            min={input.min}
            max={input.max}
            step={input.step || 1}
            unit={input.unit}
            accentColor={accentColor}
          />
        ))}
      </div>
      
      {/* Results */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className={`px-4 py-1 text-sm font-medium ${
                  accentColor === 'orange' ? 'bg-orange-50 text-orange-600' : 'bg-orange-50 text-orange-600'
                } rounded-full`}>
                  <Sparkles className="w-4 h-4 inline mr-1" />
                  Twój potencjał z Boosterem
                </span>
              </div>
            </div>
            
            {/* Result Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              <ResultCard
                icon={TrendingUp}
                label={config.resultLabels.extraRevenue}
                value={results.extraRevenue}
                suffix=" zł"
                color={accentColor === 'orange' ? 'orange' : 'violet'}
                delay={0}
              />
              <ResultCard
                icon={Users}
                label={config.resultLabels.additionalLeads}
                value={results.additionalLeads}
                suffix=""
                color="green"
                delay={0.1}
              />
              <ResultCard
                icon={Zap}
                label={config.resultLabels.timeSavings}
                value={results.timeSavings}
                suffix="h"
                color="blue"
                delay={0.2}
              />
              <ResultCard
                icon={Calculator}
                label={config.resultLabels.roi}
                value={results.roi}
                suffix="%"
                color={accentColor === 'orange' ? 'orange' : 'violet'}
                delay={0.3}
              />
            </div>
            
            {/* Summary */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-6 text-center shadow-sm"
            >
              <p className="text-zinc-600 mb-2">Potencjalny roczny przychód z Boosterem</p>
              <div className={`text-4xl font-bold ${accent.text} mb-4`}>
                {results.totalWithBooster.toLocaleString('pl-PL')} zł
              </div>
              <p className="text-sm text-zinc-500 mb-6">
                vs {results.currentRevenue.toLocaleString('pl-PL')} zł bez Boostera 
                <span className={`font-semibold ${accent.text}`}>
                  {' '}(+{Math.round((results.extraRevenue / results.currentRevenue) * 100)}%)
                </span>
              </p>
              
              <Link to="/register">
                <Button size="lg" className={`${accent.button} text-white px-8`}>
                  Zacznij za darmo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              
              <p className="text-xs text-zinc-400 mt-4">
                * Szacunki oparte na średnich wynikach użytkowników ApisAI
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Initial CTA (before results) */}
      {!showResults && (
        <div className="text-center">
          <p className="text-zinc-500 text-sm">
            Przesuń suwaki, aby zobaczyć swój potencjał
          </p>
        </div>
      )}
    </div>
  );
}
